'use client'

import { useEffect, useRef, useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import useScoutSound from './useScoutSound'

const DURATION_MS = 7400
const IDLE_DURATION_MS = 12000
const OWNERSHIP_APPROACH_MS = 900
type Playback = 'unavailable' | 'playing' | 'paused' | 'reduced'
type ScoutModule = 'radar' | 'vector' | 'hive'
type ScoutEmotion = 'curious' | 'surprised' | 'shocked' | 'proud' | 'excited'
type ScoutCaption = ScoutModule | 'ownership'
type FinaleLine = 'default' | 'fall' | 'caught'
const FINALE_LINES: Record<FinaleLine, [string, string]> = {
  default: ['Big ideas?', 'I’m all eyes.'],
  fall: ['Whoa!', 'I’m coming too!'],
  caught: ['Phew!', 'You caught me.'],
}
const SCOUT_CAPTIONS: Record<ScoutCaption, string> = {
  radar: 'Let’s see what AI sees.',
  vector: 'Let’s find the right leads.',
  hive: 'Let’s put the team to work.',
  ownership: 'The custom code is yours.',
}
type Point = { x: number; y: number }
type TravelerPose = Point & { rotation: number; scaleX: number; scaleY: number }
type ScrollAnchor = { scrollY: number; top: number }
type Hop = {
  from: TravelerPose
  target: ScoutModule
  elapsed: number
  duration: number
  height: number
}

const SCOUT_STOPS: Record<ScoutModule, Point> = {
  radar: { x: 330, y: 179 },
  vector: { x: 427, y: 187 },
  hive: { x: 370, y: 459 },
}
const SCOUT_EMOTIONS: Record<ScoutModule, ScoutEmotion> = {
  radar: 'curious',
  vector: 'surprised',
  hive: 'proud',
}

const restingPose = (point: Point): TravelerPose => ({
  ...point,
  rotation: 0,
  scaleX: 1,
  scaleY: 1,
})
const mix = (from: number, to: number, progress: number) =>
  from + (to - from) * progress
const ease = (progress: number) => progress * progress * (3 - 2 * progress)
/** Horizontal travel direction of a hop, so body lean and feet follow the stops. */
const hopDirection = (from: Point, to: Point) => Math.sign(to.x - from.x) || 1

/** Anticipation, airborne travel, and landing all share the feet origin. */
function hopPose(
  from: TravelerPose,
  to: Point,
  progress: number,
  height: number
): TravelerPose {
  const p = Math.max(0, Math.min(1, progress))
  const flight = Math.max(0, Math.min(1, (p - 0.17) / 0.63))
  const direction = hopDirection(from, to)
  const sample = (poses: [number, number][]) => {
    const index = poses.findIndex(([time]) => time >= p)
    if (index <= 0) return poses[0][1]
    const [start, startValue] = poses[index - 1]
    const [end, endValue] = poses[index]
    return mix(startValue, endValue, ease((p - start) / (end - start)))
  }
  return {
    x: mix(from.x, to.x, ease(flight)),
    y: mix(from.y, to.y, ease(flight)) - Math.sin(Math.PI * flight) * height,
    rotation: sample([
      [0, from.rotation],
      [0.17, -5 * direction],
      [0.48, 0],
      [0.8, 5 * direction],
      [0.94, -1 * direction],
      [1, 0],
    ]),
    // Preserve the starting size through takeoff, then shrink progressively.
    scaleX:
      mix(from.scaleX, 1, ease(p)) *
      sample([
        [0, 1],
        [0.17, 1.1],
        [0.32, 0.93],
        [0.72, 1],
        [0.84, 1.12],
        [0.95, 0.98],
        [1, 1],
      ]),
    scaleY:
      mix(from.scaleY, 1, ease(p)) *
      sample([
        [0, 1],
        [0.17, 0.86],
        [0.32, 1.13],
        [0.72, 1],
        [0.84, 0.84],
        [0.95, 1.03],
        [1, 1],
      ]),
  }
}

function introPose(elapsed: number): TravelerPose {
  if (elapsed < 2150) return restingPose(SCOUT_STOPS.radar)
  if (elapsed < 2850)
    return hopPose(
      restingPose(SCOUT_STOPS.radar),
      SCOUT_STOPS.vector,
      (elapsed - 2150) / 700,
      30
    )
  if (elapsed < 3600) return restingPose(SCOUT_STOPS.vector)
  if (elapsed < 4300)
    return hopPose(
      restingPose(SCOUT_STOPS.vector),
      SCOUT_STOPS.hive,
      (elapsed - 3600) / 700,
      65
    )
  return restingPose(SCOUT_STOPS.hive)
}

/** One clock for the diagram and Scout. Neither animation runs without JS. */
export default function HeroSequence() {
  const controlRef = useRef<HTMLDivElement>(null)
  const travelDialogRef = useRef<HTMLDivElement>(null)
  const replayRef = useRef<(() => void) | null>(null)
  const [playback, setPlayback] = useState<Playback>('unavailable')
  const [caption, setCaption] = useState<ScoutCaption | null>(null)
  const [finaleLine, setFinaleLine] = useState<FinaleLine>('default')
  const { enableSound, playSound, stopSound } = useScoutSound()

  useEffect(() => {
    const art = controlRef.current?.closest<HTMLElement>(
      '[data-scout-sequence]'
    )
    const diagram = art?.querySelector<SVGSVGElement>('.pmx')
    const scout = art?.querySelector<SVGSVGElement>('.hero-scout')
    const traveler = art?.querySelector<SVGGElement>('.hero-scout-traveler')
    const travelDialog = travelDialogRef.current
    const scoutClock = scout
    if (
      !art ||
      !diagram ||
      !scout ||
      !traveler ||
      !travelDialog ||
      !scoutClock?.pauseAnimations ||
      !diagram.getAnimations
    )
      return

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)')
    // The hero grid stacks the artwork under the copy (home.css).
    const stacked = window.matchMedia('(max-width: 1024px)')
    const tracks = Array.from(
      scout.querySelectorAll<SVGAnimationElement>('animate, animateTransform')
    )
    const productLinks = Array.from(
      diagram.querySelectorAll<SVGElement>('.hpe-module[data-module]')
    )
    const productTabIndexes = new Map(
      productLinks.map(product => [product, product.getAttribute('tabindex')])
    )
    const diagramAriaHidden = diagram.getAttribute('aria-hidden')
    const diagramOpacity = diagram.style.opacity
    const openingPose: TravelerPose = {
      x: 320,
      y: 490,
      rotation: 0,
      scaleX: 2.2,
      scaleY: 2.2,
    }
    // Land above the ownership message, clear of its title and product labels.
    const ownershipStop: TravelerPose = {
      ...restingPose({ x: 242, y: 449 }),
      scaleX: 0.82,
      scaleY: 0.82,
    }
    const presentArm = scout.querySelector<SVGGElement>(
      '[data-scout-present="arm"]'
    )
    const presentPalm = scout.querySelector<SVGGElement>(
      '[data-scout-present="hand"]'
    )
    const groundShadow = scout.querySelector<SVGEllipseElement>(
      '#home-scout-ground-shadow'
    )
    const fallArms = (['left', 'right'] as const).flatMap(side => {
      const path = scout.querySelector<SVGPathElement>(
        `#home-scout-arm-${side}`
      )
      const hand = scout.querySelector<SVGGElement>(`#home-scout-hand-${side}`)
      if (!path || !hand) return []
      const parts = [hand]
      const lens = scout.querySelector<SVGGElement>('#home-scout-magnifier')
      if (side === 'right' && lens) parts.push(lens)
      return [
        {
          side,
          path,
          basePath: path.getAttribute('d') ?? '',
          parts: parts.map(element => ({
            element,
            transform: element.getAttribute('transform') ?? '',
          })),
        },
      ]
    })
    let fallArmsRaised = false
    const resetFallArms = () => {
      if (!fallArmsRaised) return
      fallArmsRaised = false
      fallArms.forEach(arm => {
        arm.path.setAttribute('d', arm.basePath)
        arm.parts.forEach(({ element, transform }) => {
          element.setAttribute('transform', transform)
        })
      })
    }
    const liftFallArms = (progress: number) => {
      fallArmsRaised = true
      fallArms.forEach(arm => {
        const left = arm.side === 'left'
        // The lens hand follows a little later, then both arms straighten.
        const lift = ease(
          Math.max(0, Math.min(1, (progress - (left ? 0.1 : 0.13)) / 0.25))
        )
        const x = mix(left ? 168 : 472, left ? 157 : 474, lift)
        const y = mix(left ? 442 : 429, left ? 160 : 173, lift)
        const elbowX = mix(left ? 157 : 453, left ? 182 : 448.5, lift)
        const elbowY = mix(left ? 409 : 444, left ? 265 : 273, lift)
        arm.path.setAttribute(
          'd',
          `M ${left ? '207 370' : '423 373'} Q ${elbowX.toFixed(3)} ${elbowY.toFixed(3)} ${x.toFixed(3)} ${y.toFixed(3)}`
        )
        const flutter =
          Math.sin(progress * Math.PI * 5) * lift * (1 - progress) * 7
        arm.parts.forEach(({ element }) => {
          element.setAttribute(
            'transform',
            `translate(${x.toFixed(3)} ${y.toFixed(3)}) rotate(${((left ? 12 : -12) * lift + flutter).toFixed(3)})`
          )
        })
      })
    }
    type FootPose = { hip: number; knee: number; ankle: number }
    const feet = (['left', 'right'] as const).flatMap(side => {
      const leg = scout.querySelector<SVGGElement>(`[data-scout-leg='${side}']`)
      const path = scout.querySelector<SVGPathElement>(
        `[data-scout-leg-path='${side}']`
      )
      const boot = scout.querySelector<SVGGElement>(
        `[data-scout-boot='${side}']`
      )
      return leg && path && boot
        ? [
            {
              side,
              leg,
              path,
              boot,
              basePath: path.getAttribute('d') ?? '',
              pose: { hip: 0, knee: 0, ankle: 0 } as FootPose,
            },
          ]
        : []
    })
    let hopFeetFrom: FootPose[] = []
    // The markup ships the rest pose; only animateFeet moves the feet off it.
    let feetAtRest = true
    const gazeEyes = (['left', 'right'] as const).flatMap(side => {
      const group = scout.querySelector<SVGGElement>(
        `[data-scout-gaze='${side}']`
      )
      const eye = scout.querySelector<SVGEllipseElement>(
        `[data-scout-gaze-eye='${side}']`
      )
      return group && eye
        ? [{ group, eye, maxX: side === 'left' ? 6 : 4, x: 0, y: 0 }]
        : []
    })
    let animations: Animation[] = []
    let idleAnimations: Animation[] = []
    let frame = 0
    let elapsed = 0
    let introElapsed = 0
    let idleElapsed = 0
    let currentCaption: ScoutCaption | null = null
    let lastTime: number | null = null
    let visible = false
    let onScreen = false
    let started = false
    let scoutStarted = false
    let introComplete = false
    let disposed = false
    let currentPose = restingPose(SCOUT_STOPS.hive)
    let currentTarget: ScoutModule = 'hive'
    let interactiveHop: Hop | null = null
    let arrivalReaction: { target: ScoutModule; elapsed: number } | null = null
    let hoveredModule: ScoutModule | null = null
    let openingElapsed: number | null = null
    let openingScrollAnchor: ScrollAnchor | null = null
    let openingScroll: {
      pose: TravelerPose
      sourceTime: number
      emotion: ScoutEmotion
      progress: number
      caughtElapsed: number
    } | null = null
    let ownershipBeat: { elapsed: number; from: TravelerPose } | null = null
    let gazePointer: Point | null = null
    let finaleFromPose = restingPose(SCOUT_STOPS.hive)
    let finaleScrollAnchor: ScrollAnchor | null = null
    let finaleScrollProgress = 0
    let currentFinaleLine: FinaleLine = 'default'
    const showFinaleLine = (line: FinaleLine) => {
      if (line === currentFinaleLine) return
      currentFinaleLine = line
      setFinaleLine(line)
    }
    let finaleTransition: {
      from: number
      to: number
      elapsed: number
      duration: number
    } | null = null
    // Every path that ends the intro owes the ownership beat and finale.
    // Begin once Scout returns to Hive with no product hovered or focused.
    let finalePending = false
    const reduced = () =>
      motion.matches || document.body.classList.contains('reduce-motion')

    scoutClock.pauseAnimations()

    const stopClock = () => {
      cancelAnimationFrame(frame)
      frame = 0
      lastTime = null
      stopSound()
    }
    const showCaption = (next: ScoutCaption | null) => {
      if (next === currentCaption) return
      currentCaption = next
      setCaption(next)
      // A cleared line drops any cue still waiting for the browser to allow audio.
      if (next) playSound(next)
      else stopSound()
    }
    const resetFeet = () => {
      if (feetAtRest) return
      feetAtRest = true
      feet.forEach(foot => {
        foot.pose = { hip: 0, knee: 0, ankle: 0 }
        foot.leg.removeAttribute('transform')
        foot.boot.removeAttribute('transform')
        foot.path.setAttribute('d', foot.basePath)
      })
      groundShadow?.removeAttribute('opacity')
      groundShadow?.removeAttribute('transform')
    }
    const animateFeet = (
      progress: number,
      direction: number,
      from: FootPose[] = []
    ) => {
      const p = Math.max(0, Math.min(1, progress))
      if (p >= 1) {
        resetFeet()
        return
      }
      feetAtRest = false
      const sample = (poses: [number, number][]) => {
        const index = poses.findIndex(([time]) => time >= p)
        if (index <= 0) return poses[0][1]
        const [start, a] = poses[index - 1]
        const [end, b] = poses[index]
        return mix(a, b, ease((p - start) / (end - start)))
      }
      feet.forEach((foot, index) => {
        const left = foot.side === 'left'
        const outward = left ? -1 : 1
        const leading = direction < 0 ? left : !left
        const start = from[index] ?? { hip: 0, knee: 0, ankle: 0 }
        const hip = sample([
          [0, start.hip],
          [0.17, -outward * 5],
          [0.34, leading ? -direction * 18 : direction * 24],
          [0.56, leading ? -direction * 22 : direction * 30],
          [0.78, leading ? -direction * 4 : direction * 18],
          [0.88, leading ? -outward * 5 : direction * 3],
          [1, 0],
        ])
        const knee = sample([
          [0, start.knee],
          [0.17, 18],
          [0.34, leading ? 8 : 25],
          [0.62, leading ? 6 : 28],
          [0.8, leading ? 13 : 18],
          [0.9, leading ? 8 : 15],
          [1, 0],
        ])
        const ankle = sample([
          [0, start.ankle],
          [0.17, outward * 8],
          [0.3, leading ? -direction * 14 : direction * 24],
          [0.6, leading ? direction * 8 : direction * 18],
          [0.79, leading ? -outward * 9 : direction * 12],
          [0.89, leading ? 0 : -outward * 7],
          [1, 0],
        ])
        foot.pose = { hip, knee, ankle }
        foot.leg.setAttribute(
          'transform',
          `rotate(${hip.toFixed(3)} ${left ? '272 449' : '365 451'})`
        )
        // Only the knee control point moves; the boot stays on its leg endpoint.
        foot.path.setAttribute(
          'd',
          left
            ? `M 272 449 Q ${(269 - knee).toFixed(3)} 478 251 512`
            : `M 365 451 Q ${(371 + knee).toFixed(3)} 479 385 514`
        )
        foot.boot.setAttribute(
          'transform',
          `rotate(${ankle.toFixed(3)} ${left ? '251 512' : '385 514'})`
        )
      })
      const airborne = Math.sin(
        Math.PI * Math.max(0, Math.min(1, (p - 0.17) / 0.63))
      )
      groundShadow?.setAttribute('opacity', (1 - airborne * 0.85).toFixed(3))
      const shadowScale = 1 - airborne * 0.35
      groundShadow?.setAttribute(
        'transform',
        `translate(320 556) scale(${shadowScale.toFixed(3)} 1) translate(-320 -556)`
      )
    }
    const resetActing = () => {
      delete art.dataset.scoutOpening
      delete art.dataset.scoutOwnership
      delete art.dataset.scoutGesture
      presentArm?.removeAttribute('transform')
      presentPalm?.setAttribute('transform', 'translate(100 350) rotate(-64)')
    }
    const presentHand = (progress: number) => {
      art.dataset.scoutGesture = 'present'
      const scale = mix(0.75, 1, progress)
      presentArm?.setAttribute(
        'transform',
        `translate(207 370) rotate(${mix(-72, 0, progress).toFixed(3)}) scale(${scale.toFixed(3)}) translate(-207 -370)`
      )
      presentPalm?.setAttribute(
        'transform',
        `translate(100 350) rotate(${mix(57, -64, progress).toFixed(3)})`
      )
    }
    const resetGaze = () => {
      gazePointer = null
      gazeEyes.forEach(eye => {
        eye.x = 0
        eye.y = 0
        eye.group.removeAttribute('transform')
      })
    }
    const onGazePointer = (event: PointerEvent) => {
      if (
        (event.pointerType !== 'mouse' && event.pointerType !== 'pen') ||
        art.dataset.scoutFinale !== 'ready' ||
        finaleScrollProgress > 0 ||
        !visible ||
        document.hidden ||
        reduced()
      )
        return
      gazePointer = { x: event.clientX, y: event.clientY }
    }
    const advanceGaze = (delta: number) => {
      if (art.dataset.scoutFinale !== 'ready' || !gazePointer) return
      const smoothing = 1 - Math.exp(-delta / 90)
      const pointer = new DOMPoint(gazePointer.x, gazePointer.y)
      gazeEyes.forEach(eye => {
        const matrix = eye.eye.getScreenCTM()
        if (!matrix || matrix.a * matrix.d === matrix.b * matrix.c) return
        const local = pointer.matrixTransform(matrix.inverse())
        const x =
          Math.max(
            -1,
            Math.min(1, (local.x - eye.eye.cx.baseVal.value) / 140)
          ) * eye.maxX
        const y =
          Math.max(
            -1,
            Math.min(1, (local.y - eye.eye.cy.baseVal.value) / 140)
          ) * 4
        eye.x = mix(eye.x, x, smoothing)
        eye.y = mix(eye.y, y, smoothing)
        eye.group.setAttribute(
          'transform',
          `translate(${eye.x.toFixed(3)} ${eye.y.toFixed(3)})`
        )
      })
    }
    const beginScout = () => {
      scoutClock.setCurrentTime(0)
      tracks.forEach(track => track.beginElement())
      scoutStarted = true
    }
    const seekScout = (seconds: number, inspect = false) => {
      scoutClock.setCurrentTime(seconds)
      if (inspect && seconds >= 1.25 && seconds <= 1.65) {
        if (art.dataset.scoutLens !== 'inspect')
          art.dataset.scoutLens = 'inspect'
      } else if (art.dataset.scoutLens) delete art.dataset.scoutLens
    }
    const seek = () => {
      animations.forEach(animation => {
        animation.currentTime = elapsed
      })
      seekScout(elapsed / 1000, !introComplete)
    }
    // Each action has time to read, hold, and settle within the shared idle loop.
    // Values seek only Scout's source poses; the finished diagram stays at 7.4s.
    const idleActions: Record<ScoutModule, [number, number][]> = {
      radar: [
        [0, 1.82],
        [2000, 1.82],
        [3400, 1.05],
        [4400, 1.45],
        [6500, 1.45],
        [8000, 1.82],
        [12000, 1.82],
      ],
      vector: [
        [0, 3.6],
        [800, 2.9],
        [2000, 3.25],
        [4400, 3.25],
        [5600, 3.45],
        [8500, 3.45],
        [10500, 3.6],
        [12000, 3.6],
      ],
      hive: [
        [0, 6.7],
        [1000, 5.5],
        [2200, 5.95],
        [5000, 5.95],
        [6200, 6.3],
        [9200, 6.3],
        [11000, 6.7],
        [12000, 6.7],
      ],
    }
    const reactionActions: Record<ScoutModule, [number, number][]> = {
      radar: [
        [0, 1.05],
        [450, 1.45],
        [1000, 1.45],
        [1350, 1.82],
        [1800, 1.82],
      ],
      vector: [
        [0, 2.7],
        [450, 3.25],
        [800, 3.25],
        [1050, 3.45],
        [1350, 3.45],
        [1800, 3.6],
      ],
      hive: [
        [0, 5.3],
        [250, 5.6],
        [1350, 6.3],
        [1800, 6.7],
      ],
    }
    const seekReactionScout = () => {
      if (!arrivalReaction) return
      const { target, elapsed: reactionElapsed } = arrivalReaction
      const poses = reactionActions[target]
      const index = poses.findIndex(([time]) => time >= reactionElapsed)
      let seconds = poses[0][1]
      if (index > 0) {
        const [start, from] = poses[index - 1]
        const [end, to] = poses[index]
        seconds = mix(from, to, ease((reactionElapsed - start) / (end - start)))
      }
      seekScout(seconds, target === 'radar')
      setEmotion(
        target === 'radar'
          ? reactionElapsed < 600
            ? SCOUT_EMOTIONS.radar
            : 'surprised'
          : target === 'vector'
            ? reactionElapsed < 850
              ? SCOUT_EMOTIONS.vector
              : 'proud'
            : reactionElapsed < 1100
              ? 'excited'
              : 'proud'
      )
    }
    const seekIdleScout = (blend = 1) => {
      if (!scoutStarted) beginScout()
      const poses = idleActions[currentTarget]
      const index = poses.findIndex(([time]) => time >= idleElapsed)
      let seconds = poses[0][1]
      if (index > 0) {
        const [start, from] = poses[index - 1]
        const [end, to] = poses[index]
        seconds = mix(from, to, ease((idleElapsed - start) / (end - start)))
      }
      seekScout(mix(7.4, seconds, blend), currentTarget === 'radar')
    }
    const placeScout = (pose: TravelerPose) => {
      currentPose = pose
      traveler.setAttribute(
        'transform',
        `translate(${pose.x.toFixed(3)} ${pose.y.toFixed(3)}) rotate(${pose.rotation.toFixed(3)}) scale(${pose.scaleX.toFixed(3)} ${pose.scaleY.toFixed(3)})`
      )
    }
    const setProductsAccessible = (accessible: boolean) => {
      if (!accessible) diagram.setAttribute('aria-hidden', 'true')
      else if (diagramAriaHidden === null)
        diagram.removeAttribute('aria-hidden')
      else diagram.setAttribute('aria-hidden', diagramAriaHidden)
      productTabIndexes.forEach((tabIndex, product) => {
        if (!accessible) product.setAttribute('tabindex', '-1')
        else if (tabIndex === null) product.removeAttribute('tabindex')
        else product.setAttribute('tabindex', tabIndex)
      })
    }
    const resetFinale = () => {
      openingScrollAnchor = null
      openingScroll = null
      art.style.removeProperty('--scout-opening-opacity')
      art.style.removeProperty('--scout-opening-visibility')
      finaleTransition = null
      finaleScrollAnchor = null
      finaleScrollProgress = 0
      showFinaleLine('default')
      resetFallArms()
      art.style.removeProperty('--scout-finale-copy-opacity')
      art.style.removeProperty('--scout-finale-copy-visibility')
      art.style.removeProperty('--scout-finale-dialog-opacity')
      art.style.removeProperty('--scout-finale-dialog-visibility')
      travelDialog.style.removeProperty('transform')
      travelDialog.style.removeProperty('opacity')
      travelDialog.style.removeProperty('visibility')
      resetGaze()
      delete art.dataset.scoutFinale
      diagram.style.opacity = diagramOpacity
      setProductsAccessible(true)
    }
    const placeFinale = (progress: number) => {
      placeScout({
        x: mix(finaleFromPose.x, 320, progress),
        y: mix(finaleFromPose.y, 475, progress),
        rotation: mix(finaleFromPose.rotation, 0, progress),
        scaleX: mix(finaleFromPose.scaleX, 2, progress),
        scaleY: mix(finaleFromPose.scaleY, 2, progress),
      })
      diagram.style.opacity = (1 - progress).toFixed(3)
    }
    const enterFinale = () => {
      if (diagram.contains(document.activeElement)) return
      finalePending = false
      finaleFromPose = { ...currentPose }
      resetGaze()
      setProductsAccessible(false)
      art.dataset.scoutFinale = 'entering'
      finaleTransition = { from: 0, to: 1, elapsed: 0, duration: 1000 }
    }
    const enterOwnership = () => {
      clearIdle()
      resetFeet()
      ownershipBeat = { elapsed: 0, from: { ...currentPose } }
      art.dataset.scoutBeat = 'ownership'
      art.dataset.scoutOwnership = 'notice'
      art.dataset.sequencePhase = 'ownership'
      beginScout()
      seekScout(2.7)
      setEmotion('curious')
    }
    const setEmotion = (emotion: ScoutEmotion) => {
      if (art.dataset.scoutEmotion !== emotion)
        art.dataset.scoutEmotion = emotion
    }
    const scrollProgress = (
      rect: DOMRect,
      anchor: ScrollAnchor,
      threshold = 0
    ) => {
      const top = rect.top + window.scrollY
      // Side by side, let the whole artwork enter before Scout falls. Stacked,
      // scrolling up to the artwork is viewing it: fall only once its middle
      // passes the viewport's vertical center, which short screens reach too.
      // Follow layout shifts without turning a resize into a new scroll origin.
      const start = Math.max(
        anchor.scrollY + top - anchor.top,
        stacked.matches
          ? top + rect.height / 2 - window.innerHeight / 2
          : top + rect.height - window.innerHeight + 32
      )
      const distance = Math.max(180, Math.min(420, rect.height * 0.7))
      return Math.max(
        0,
        Math.min(1, (window.scrollY - start - threshold) / distance)
      )
    }
    const placeScrollFall = (
      p: number,
      pose: TravelerPose,
      sourceTime: number,
      emotion: ScoutEmotion
    ) => {
      const reaction = ease(Math.min(1, p / 0.1))
      const anticipation = Math.sin(Math.PI * Math.min(1, p / 0.22))
      const flight = Math.max(0, (p - 0.18) / 0.82)
      const fall = flight * flight
      // Every pose is a function of scroll position, so reversing is exact.
      placeScout({
        x: pose.x - anticipation * 9 + fall * 44,
        y: pose.y + anticipation * 9 + fall * 420,
        rotation: pose.rotation,
        scaleX: pose.scaleX * (1 + anticipation * 0.08 - flight * 0.12),
        scaleY:
          pose.scaleY *
          (1 - anticipation * 0.1 + Math.sin(Math.PI * flight) * 0.15),
      })
      // Match the 640-unit SVG stage without inheriting Scout's squash/stretch.
      travelDialog.style.transform = `translate(${((currentPose.x - 100) / 6.4).toFixed(3)}cqw, ${((currentPose.y - 110) / 6.4).toFixed(3)}cqw) translate(-100%, -50%)`
      travelDialog.style.opacity = reaction.toFixed(3)
      travelDialog.style.visibility = reaction > 0 ? 'visible' : 'hidden'
      if (p > 0) {
        resetGaze()
        animateFeet(p * 0.62, 1)
        // Finish the source tracks before posing the arms, so SMIL cannot
        // override their paths. Reverse through the same rest pose on return.
        if (p <= 0.1) resetFallArms()
        seekScout(mix(sourceTime, 7.4, reaction))
        if (p > 0.1) liftFallArms(p)
        setEmotion(p > 0.1 ? 'shocked' : emotion)
      } else {
        resetFallArms()
        resetFeet()
        seekScout(sourceTime)
        setEmotion(emotion)
      }
    }
    const placeScrollFinale = (rect: DOMRect) => {
      if (!finaleScrollAnchor) return
      const p = scrollProgress(rect, finaleScrollAnchor)
      if (p !== finaleScrollProgress) {
        showFinaleLine(p < finaleScrollProgress ? 'caught' : 'fall')
      }
      finaleScrollProgress = p
      const reaction = ease(Math.min(1, p / 0.1))
      idleAnimations.forEach(animation => {
        animation.currentTime = idleElapsed * (1 - reaction)
      })
      art.style.setProperty(
        '--scout-finale-copy-opacity',
        (1 - ease(Math.min(1, p / 0.24))).toFixed(3)
      )
      art.style.setProperty(
        '--scout-finale-copy-visibility',
        p >= 0.24 ? 'hidden' : 'visible'
      )
      // Hand the fixed greeting over to a bubble that travels with Scout.
      const dialogOpacity = 1 - reaction
      art.style.setProperty(
        '--scout-finale-dialog-opacity',
        dialogOpacity.toFixed(3)
      )
      art.style.setProperty(
        '--scout-finale-dialog-visibility',
        dialogOpacity > 0 ? 'visible' : 'hidden'
      )
      placeScrollFall(
        p,
        { ...restingPose({ x: 320, y: 475 }), scaleX: 2, scaleY: 2 },
        scoutClock.getCurrentTime(),
        'proud'
      )
    }
    const sampleOpeningScroll = (delta = 0) => {
      if (
        openingElapsed === null ||
        openingElapsed >= 1900 ||
        !openingScrollAnchor
      )
        return false
      const p = scrollProgress(
        art.getBoundingClientRect(),
        openingScrollAnchor,
        12
      )
      if (!openingScroll) {
        if (p === 0) return false
        openingScroll = {
          pose: { ...currentPose },
          sourceTime: scoutClock.getCurrentTime(),
          emotion: openingElapsed < 1350 ? 'excited' : 'proud',
          progress: 0,
          caughtElapsed: 0,
        }
      }
      if (p !== openingScroll.progress)
        showFinaleLine(p < openingScroll.progress ? 'caught' : 'fall')
      openingScroll.progress = p
      art.dataset.scoutOpening = 'scrolling'
      placeScrollFall(
        p,
        openingScroll.pose,
        openingScroll.sourceTime,
        openingScroll.emotion
      )
      const caught = currentFinaleLine === 'caught'
      art.style.setProperty(
        '--scout-opening-opacity',
        caught ? '0' : (1 - ease(Math.min(1, p / 0.1))).toFixed(3)
      )
      art.style.setProperty(
        '--scout-opening-visibility',
        caught || p >= 0.1 ? 'hidden' : 'visible'
      )
      // Let the catch read at the restored pose before resuming the greeting.
      if (caught) {
        travelDialog.style.opacity = '1'
        travelDialog.style.visibility = 'visible'
      }
      openingScroll.caughtElapsed =
        p === 0 ? openingScroll.caughtElapsed + delta : 0
      if (openingScroll.caughtElapsed >= 600) {
        openingScroll = null
        art.dataset.scoutOpening = 'greeting'
        art.style.removeProperty('--scout-opening-opacity')
        art.style.removeProperty('--scout-opening-visibility')
        travelDialog.style.opacity = '0'
        travelDialog.style.visibility = 'hidden'
        showFinaleLine('default')
      }
      return true
    }
    const clearIdle = () => {
      idleAnimations.forEach(animation => animation.cancel())
      idleAnimations = []
    }
    const startIdle = () => {
      if (idleAnimations.length) return
      const torso = scout.querySelector('#home-scout-idle-torso')
      const eyes = scout.querySelectorAll(
        '#home-scout-idle-eye-left, #home-scout-idle-eye-right'
      )
      if (torso) {
        idleAnimations.push(
          torso.animate(
            [
              { transform: 'translate(0px, 0px) rotate(0deg)', offset: 0 },
              {
                transform: 'translate(-1px, -5px) rotate(-1deg)',
                offset: 0.15,
              },
              { transform: 'translate(-3px, 0px) rotate(-3deg)', offset: 0.3 },
              {
                transform: 'translate(-3px, -4px) rotate(-3deg)',
                offset: 0.43,
              },
              { transform: 'translate(0px, 0px) rotate(0deg)', offset: 0.6 },
              { transform: 'translate(1px, -5px) rotate(2deg)', offset: 0.78 },
              { transform: 'translate(0px, 0px) rotate(0deg)', offset: 1 },
            ],
            {
              duration: IDLE_DURATION_MS,
              iterations: Infinity,
              easing: 'ease-in-out',
            }
          )
        )
      }
      eyes.forEach(eye => {
        idleAnimations.push(
          eye.animate(
            [
              { transform: 'scaleY(1)', offset: 0 },
              { transform: 'scaleY(1)', offset: 0.26 },
              { transform: 'scaleY(0.08)', offset: 0.27 },
              { transform: 'scaleY(1)', offset: 0.285 },
              { transform: 'scaleY(1)', offset: 0.7 },
              { transform: 'scaleY(0.08)', offset: 0.71 },
              { transform: 'scaleY(1)', offset: 0.725 },
              { transform: 'scaleY(1)', offset: 1 },
            ],
            { duration: IDLE_DURATION_MS, iterations: Infinity }
          )
        )
      })
      idleAnimations.forEach(animation => animation.pause())
    }
    const tick = (now: number) => {
      const delta = lastTime === null ? 0 : now - lastTime
      lastTime = now
      // Scroll owns the greeting pose and freezes its clock until the catch.
      if (sampleOpeningScroll(delta)) {
        frame = requestAnimationFrame(tick)
        return
      }
      if (openingElapsed !== null) {
        openingElapsed = Math.min(2900, openingElapsed + delta)
        if (openingElapsed < 1900) {
          const size = mix(2.35, 2.2, ease(Math.min(1, openingElapsed / 500)))
          placeScout({ ...openingPose, scaleX: size, scaleY: size })
          seekScout(mix(5.3, 6.7, openingElapsed / 1900))
          setEmotion(openingElapsed < 1350 ? 'excited' : 'proud')
        } else {
          art.dataset.scoutOpening = 'departing'
          const progress = (openingElapsed - 1900) / 1000
          placeScout(hopPose(openingPose, SCOUT_STOPS.radar, progress, 35))
          animateFeet(progress, hopDirection(openingPose, SCOUT_STOPS.radar))
          seekScout(mix(6.7, 7.4, ease(progress)))
          diagram.style.opacity = ease(progress).toFixed(3)
          setEmotion('excited')
        }
        if (openingElapsed >= 2900) {
          openingElapsed = null
          openingScrollAnchor = null
          delete art.dataset.scoutBeat
          delete art.dataset.scoutOpening
          resetFeet()
          diagram.style.opacity = diagramOpacity
          setProductsAccessible(true)
          beginScout()
          seek()
          placeScout(restingPose(SCOUT_STOPS.radar))
          setEmotion('curious')
        }
      } else if (ownershipBeat) {
        ownershipBeat.elapsed += delta
        // Reach the ownership line sooner, retaining the original presentation hold.
        const time = Math.min(
          3800,
          Math.min(
            1650,
            ownershipBeat.elapsed * (1650 / OWNERSHIP_APPROACH_MS)
          ) + Math.max(0, ownershipBeat.elapsed - OWNERSHIP_APPROACH_MS)
        )
        const noticedPose = {
          ...ownershipBeat.from,
          x: ownershipBeat.from.x - 3,
          y: ownershipBeat.from.y + 2,
          rotation: -3,
        }
        if (time < 600) {
          const notice = ease(time / 600)
          art.dataset.scoutOwnership = 'notice'
          placeScout({
            ...ownershipBeat.from,
            x: mix(ownershipBeat.from.x, noticedPose.x, notice),
            y: mix(ownershipBeat.from.y, noticedPose.y, notice),
            rotation: mix(ownershipBeat.from.rotation, -3, notice),
          })
          seekScout(mix(2.7, 3.25, notice))
          setEmotion(time < 400 ? 'curious' : 'surprised')
        } else if (time < 1650) {
          art.dataset.scoutOwnership = 'travel'
          const progress = (time - 600) / 1050
          const pose = hopPose(noticedPose, ownershipStop, progress, 48)
          pose.scaleX *= mix(1, ownershipStop.scaleX, ease(progress))
          pose.scaleY *= mix(1, ownershipStop.scaleY, ease(progress))
          // A short step back loads the diagonal bound toward the ownership line.
          pose.x -=
            hopDirection(noticedPose, ownershipStop) *
            Math.sin(Math.PI * Math.min(1, progress / 0.34)) *
            7
          placeScout(pose)
          animateFeet(progress, hopDirection(noticedPose, ownershipStop))
          seekScout(time < 850 ? mix(3.25, 3.6, ease((time - 600) / 250)) : 7.4)
          setEmotion(time < 850 ? 'surprised' : 'excited')
        } else {
          resetFeet()
          art.dataset.scoutOwnership = time < 3150 ? 'present' : 'settle'
          const present =
            time < 3150
              ? ease(Math.min(1, (time - 1650) / 650))
              : 1 - ease((time - 3150) / 650)
          const nod = Math.sin(
            Math.PI * Math.max(0, Math.min(1, (time - 2550) / 550))
          )
          placeScout({
            ...ownershipStop,
            y: ownershipStop.y + nod * 2.5,
            rotation: -present * 2 + nod * 1.8,
          })
          seekScout(7.4)
          presentHand(present)
          setEmotion('proud')
        }
        if (time >= 3800) {
          ownershipBeat = null
          resetActing()
          delete art.dataset.scoutBeat
          art.dataset.sequencePhase = 'idle'
          idleElapsed = 0
          beginScout()
          startIdle()
          seekIdleScout(0)
          enterFinale()
        }
      } else if (finaleTransition) {
        finaleTransition.elapsed = Math.min(
          finaleTransition.duration,
          finaleTransition.elapsed + delta
        )
        placeFinale(
          mix(
            finaleTransition.from,
            finaleTransition.to,
            ease(finaleTransition.elapsed / finaleTransition.duration)
          )
        )
        idleElapsed = (idleElapsed + delta) % IDLE_DURATION_MS
        seekIdleScout(
          ease(finaleTransition.elapsed / finaleTransition.duration)
        )
        idleAnimations.forEach(animation => {
          animation.currentTime = idleElapsed
        })
        if (finaleTransition.elapsed >= finaleTransition.duration) {
          finaleTransition = null
          art.dataset.scoutFinale = 'ready'
          finaleScrollAnchor = {
            scrollY: window.scrollY,
            top: art.getBoundingClientRect().top + window.scrollY,
          }
        }
      } else if (art.dataset.scoutFinale === 'ready') {
        const rect = art.getBoundingClientRect()
        // Freeze the idle clock during the fall; it resumes at the rest pose.
        if (finaleScrollProgress === 0)
          idleElapsed = (idleElapsed + delta) % IDLE_DURATION_MS
        seekIdleScout()
        idleAnimations.forEach(animation => {
          animation.currentTime = idleElapsed
        })
        placeScrollFinale(rect)
      } else if (interactiveHop) {
        interactiveHop.elapsed = Math.min(
          interactiveHop.duration,
          interactiveHop.elapsed + delta
        )
        placeScout(
          hopPose(
            interactiveHop.from,
            SCOUT_STOPS[interactiveHop.target],
            interactiveHop.elapsed / interactiveHop.duration,
            interactiveHop.height
          )
        )
        animateFeet(
          interactiveHop.elapsed / interactiveHop.duration,
          hopDirection(interactiveHop.from, SCOUT_STOPS[interactiveHop.target]),
          hopFeetFrom
        )
        if (interactiveHop.elapsed >= interactiveHop.duration) {
          currentTarget = interactiveHop.target
          interactiveHop = null
          beginScout()
          arrivalReaction = { target: currentTarget, elapsed: 0 }
          art.dataset.sequencePhase = 'reaction'
          seekReactionScout()
        }
      } else if (arrivalReaction) {
        arrivalReaction.elapsed = Math.min(
          1800,
          arrivalReaction.elapsed + delta
        )
        seekReactionScout()
        if (arrivalReaction.elapsed >= 1800) {
          arrivalReaction = null
          idleElapsed = 0
          art.dataset.sequencePhase = 'idle'
          startIdle()
          seekIdleScout()
          idleAnimations.forEach(animation => {
            animation.currentTime = 0
          })
        }
      } else if (introComplete) {
        if (
          finalePending &&
          currentTarget === 'hive' &&
          !hoveredModule &&
          !focusedModule() &&
          !diagram.contains(document.activeElement)
        ) {
          enterOwnership()
        } else {
          idleElapsed = (idleElapsed + delta) % IDLE_DURATION_MS
          seekIdleScout()
          idleAnimations.forEach(animation => {
            animation.currentTime = idleElapsed
          })
        }
      } else {
        introElapsed += delta
        // Hold Vector's recognition pose long enough to read its line.
        // Advance the shared clock twice as fast after Scout lands at Hive.
        const sequenceTime =
          introElapsed - Math.min(1400, Math.max(0, introElapsed - 3250))
        elapsed = Math.min(
          DURATION_MS,
          sequenceTime + Math.max(0, sequenceTime - 4300)
        )
        seek()
        placeScout(introPose(elapsed))
        if (elapsed >= 2150 && elapsed < 2850)
          animateFeet(
            (elapsed - 2150) / 700,
            hopDirection(SCOUT_STOPS.radar, SCOUT_STOPS.vector)
          )
        else if (elapsed >= 3600 && elapsed < 4300)
          animateFeet(
            (elapsed - 3600) / 700,
            hopDirection(SCOUT_STOPS.vector, SCOUT_STOPS.hive)
          )
        else resetFeet()
        setEmotion(
          elapsed < 2150
            ? 'curious'
            : elapsed < 2850
              ? 'excited'
              : elapsed < 3600
                ? 'surprised'
                : elapsed < 4300
                  ? 'excited'
                  : 'proud'
        )
        if (elapsed >= DURATION_MS) {
          introComplete = true
          currentTarget = 'hive'
          art.dataset.sequencePhase = 'idle'
          startIdle()
          seekIdleScout()
          idleAnimations.forEach(animation => {
            animation.currentTime = idleElapsed
          })
          finalePending = true
        }
      }
      showCaption(
        openingElapsed !== null || finaleTransition || art.dataset.scoutFinale
          ? null
          : ownershipBeat
            ? ownershipBeat.elapsed >= OWNERSHIP_APPROACH_MS
              ? 'ownership'
              : null
            : interactiveHop
              ? null
              : introComplete
                ? currentTarget
                : elapsed < 2150
                  ? 'radar'
                  : elapsed >= 2850 && elapsed < 3600
                    ? 'vector'
                    : elapsed >= 4300
                      ? 'hive'
                      : null
      )
      advanceGaze(delta)
      frame = requestAnimationFrame(tick)
    }
    const start = () => {
      resetFinale()
      clearIdle()
      resetFeet()
      resetActing()
      interactiveHop = null
      arrivalReaction = null
      ownershipBeat = null
      openingElapsed = 0
      finalePending = false
      currentTarget = 'radar'
      // Flush the old CSS pass before collecting the new one on Replay.
      art.classList.remove('is-live')
      diagram
        .getAnimations({ subtree: true })
        .forEach(animation => animation.cancel())
      art.classList.add('is-live')
      animations = Array.from(diagram.querySelectorAll('.pmx-animate')).flatMap(
        element => element.getAnimations()
      )
      animations.forEach(animation => animation.pause())
      beginScout()
      elapsed = 0
      introElapsed = 0
      idleElapsed = 0
      showCaption(null)
      started = true
      introComplete = false
      art.dataset.sequencePhase = 'sequence'
      art.dataset.scoutBeat = 'opening'
      art.dataset.scoutOpening = 'greeting'
      diagram.style.opacity = '0'
      setProductsAccessible(false)
      seek()
      seekScout(5.3)
      placeScout({ ...openingPose, scaleX: 2.35, scaleY: 2.35 })
      setEmotion('excited')
      openingScrollAnchor = {
        scrollY: window.scrollY,
        top: art.getBoundingClientRect().top + window.scrollY,
      }
    }
    const sync = () => {
      stopClock()
      if (reduced()) {
        // Removing the CSS pass restores the complete diagram; SMIL's
        // fill="remove" restores Scout's neutral base artwork at the end.
        resetFinale()
        art.classList.remove('is-live')
        animations.forEach(animation => animation.cancel())
        animations = []
        clearIdle()
        resetFeet()
        resetActing()
        interactiveHop = null
        arrivalReaction = null
        openingElapsed = null
        ownershipBeat = null
        showCaption(null)
        delete art.dataset.scoutBeat
        currentTarget = 'hive'
        elapsed = DURATION_MS
        idleElapsed = 0
        seekScout(DURATION_MS / 1000)
        placeScout(restingPose(SCOUT_STOPS.hive))
        setEmotion('proud')
        introComplete = true
        finalePending = true
        art.dataset.sequencePhase = 'idle'
        art.dataset.sequenceState = 'reduced'
        setPlayback('reduced')
        return
      }
      // Sample before the visibility pause/resume, including large scroll jumps.
      const openingScrollActive = sampleOpeningScroll()
      if (art.dataset.scoutFinale === 'ready') {
        const rect = art.getBoundingClientRect()
        seekIdleScout()
        placeScrollFinale(rect)
      }
      // A scroll reaction keeps the clock while any artwork is on screen, so
      // Scout's fall follows the scroll without a scroll listener.
      const scrollOnScreen =
        onScreen && (openingScrollActive || art.dataset.scoutFinale === 'ready')
      if ((!visible && !scrollOnScreen) || document.hidden) {
        resetGaze()
        art.dataset.sequenceState = 'paused'
        setPlayback('paused')
        return
      }
      if (
        introComplete &&
        !interactiveHop &&
        !arrivalReaction &&
        !ownershipBeat &&
        !finaleTransition &&
        art.dataset.scoutFinale !== 'ready'
      ) {
        startIdle()
        seekIdleScout()
        idleAnimations.forEach(animation => {
          animation.currentTime = idleElapsed
        })
      } else if (!started) start()
      art.dataset.sequenceState = 'playing'
      setPlayback('playing')
      // Audio starts with the visible tour, or at the first click, tap or key.
      enableSound()
      frame = requestAnimationFrame(tick)
    }
    replayRef.current = () => {
      if (reduced()) return
      // Replay hides its speech bubble; keep keyboard focus on the hero.
      if (controlRef.current?.contains(document.activeElement))
        art.focus({ preventScroll: true })
      stopClock()
      start()
      sync()
    }
    const moduleFromElement = (
      element: EventTarget | null
    ): ScoutModule | null => {
      if (!(element instanceof Element)) return null
      const product = element.closest<SVGElement>('.hpe-module[data-module]')
      if (!product || !diagram.contains(product)) return null
      const name = product.dataset.module
      return name === 'radar' || name === 'vector' || name === 'hive'
        ? name
        : null
    }
    const focusedModule = () =>
      document.activeElement?.matches(':focus-visible')
        ? moduleFromElement(document.activeElement)
        : null
    const visit = (target: ScoutModule, bounce = false) => {
      if (reduced() || openingElapsed !== null || art.dataset.scoutFinale)
        return
      if (interactiveHop?.target === target) return
      if (
        introComplete &&
        !interactiveHop &&
        currentTarget === target &&
        !bounce
      )
        return

      // An explicit visit finishes the diagram without moving Scout's feet.
      // A new hop can then begin from any in-flight pose without snapping.
      stopClock()
      clearIdle()
      hopFeetFrom = feet.map(foot => ({ ...foot.pose }))
      resetActing()
      arrivalReaction = null
      ownershipBeat = null
      delete art.dataset.scoutBeat
      elapsed = DURATION_MS
      // Finish the diagram without snapping Scout's ongoing gesture to rest.
      animations.forEach(animation => {
        animation.currentTime = elapsed
      })
      showCaption(null)
      started = true
      if (!introComplete) finalePending = true
      introComplete = true
      idleElapsed = 0
      const to = SCOUT_STOPS[target]
      const distance = Math.hypot(to.x - currentPose.x, to.y - currentPose.y)
      interactiveHop = {
        from: { ...currentPose },
        target,
        elapsed: 0,
        duration: Math.min(850, 650 + distance * 0.5),
        height: Math.min(
          75,
          38 + distance * 0.1,
          Math.max(0, Math.min(currentPose.y, to.y) - 150)
        ),
      }
      art.dataset.sequencePhase = 'hop'
      setEmotion('excited')
      sync()
    }
    const moduleCleanups = productLinks.map(product => {
      const name = moduleFromElement(product)
      const onPointerEnter = (event: PointerEvent) => {
        if (
          !name ||
          (event.pointerType !== 'mouse' && event.pointerType !== 'pen')
        )
          return
        hoveredModule = name
        visit(name, true)
      }
      const onPointerLeave = (event: PointerEvent) => {
        if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return
        hoveredModule = moduleFromElement(event.relatedTarget)
        visit(focusedModule() ?? hoveredModule ?? 'hive')
      }
      const onFocus = () => {
        if (name) visit(name, true)
      }
      const onBlur = () => {
        // The newly focused link becomes active after the blur event finishes.
        queueMicrotask(() => {
          if (!disposed) visit(focusedModule() ?? hoveredModule ?? 'hive')
        })
      }
      product.addEventListener('pointerenter', onPointerEnter)
      product.addEventListener('pointerleave', onPointerLeave)
      product.addEventListener('focus', onFocus)
      product.addEventListener('blur', onBlur)
      return () => {
        product.removeEventListener('pointerenter', onPointerEnter)
        product.removeEventListener('pointerleave', onPointerLeave)
        product.removeEventListener('focus', onFocus)
        product.removeEventListener('blur', onBlur)
      }
    })
    const observer = new IntersectionObserver(
      entries => {
        visible = entries.some(
          entry => entry.isIntersecting && entry.intersectionRatio >= 0.25
        )
        onScreen = entries.some(entry => entry.isIntersecting)
        sync()
      },
      { threshold: [0, 0.25] }
    )
    observer.observe(art)
    const onBodyChange = () => {
      // Only a change to the motion preference should interrupt the clock.
      if (reduced() !== (art.dataset.sequenceState === 'reduced')) sync()
    }
    const bodyObserver = new MutationObserver(onBodyChange)
    bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    })
    window.addEventListener('pointermove', onGazePointer)
    document.documentElement.addEventListener('pointerleave', resetGaze)
    window.addEventListener('blur', resetGaze)
    motion.addEventListener('change', sync)
    document.addEventListener('visibilitychange', sync)
    sync()

    return () => {
      disposed = true
      stopClock()
      moduleCleanups.forEach(cleanup => cleanup())
      observer.disconnect()
      bodyObserver.disconnect()
      window.removeEventListener('pointermove', onGazePointer)
      document.documentElement.removeEventListener('pointerleave', resetGaze)
      window.removeEventListener('blur', resetGaze)
      motion.removeEventListener('change', sync)
      document.removeEventListener('visibilitychange', sync)
      animations.forEach(animation => animation.cancel())
      clearIdle()
      resetFinale()
      resetFeet()
      resetActing()
      seekScout(DURATION_MS / 1000)
      placeScout(restingPose(SCOUT_STOPS.hive))
      art.classList.remove('is-live')
      delete art.dataset.sequenceState
      delete art.dataset.sequencePhase
      delete art.dataset.scoutEmotion
      delete art.dataset.scoutBeat
      replayRef.current = null
    }
  }, [enableSound, playSound, stopSound])

  const captionLabel = caption === 'ownership' ? 'Your build' : caption
  const announcement = caption
    ? `${captionLabel}: ${SCOUT_CAPTIONS[caption]}`
    : finaleLine === 'default'
      ? ''
      : FINALE_LINES[finaleLine].join(' ')
  const restingLine = finaleLine === 'caught' ? 'caught' : 'default'
  const travelingLine = finaleLine === 'caught' ? 'caught' : 'fall'

  return (
    <>
      {/* Bubbles mount or move per line, so a persistent region announces them. */}
      <p className='sr-only' aria-live='polite'>
        {announcement}
      </p>
      {caption && (
        <p
          className='hero-scout-caption'
          data-scout-caption={caption}
          aria-hidden='true'
        >
          <span>{captionLabel}</span>
          {SCOUT_CAPTIONS[caption]}
        </p>
      )}
      <div className='hero-scout-dialog hero-scout-dialog--finale'>
        <p>
          <span>{FINALE_LINES[restingLine][0]}</span>
          <em>{FINALE_LINES[restingLine][1]}</em>
        </p>
        <div className='hero-sequence-control' ref={controlRef}>
          <Button
            type='button'
            variant='accent'
            size='icon'
            className='hero-sequence-toggle'
            hidden={playback === 'unavailable' || playback === 'reduced'}
            aria-label='Replay Scout’s tour'
            title='Replay Scout’s tour with sound'
            aria-controls='engineart'
            onClick={() => replayRef.current?.()}
          >
            <RotateCcw size={16} aria-hidden='true' />
          </Button>
        </div>
      </div>
      <div
        className='hero-scout-dialog hero-scout-dialog--travel'
        ref={travelDialogRef}
        aria-hidden='true'
      >
        <p>
          <span>{FINALE_LINES[travelingLine][0]}</span>
          <em>{FINALE_LINES[travelingLine][1]}</em>
        </p>
      </div>
    </>
  )
}
